import React, { useState, useMemo } from 'react';
import { Plus, Pill, Leaf, Sparkles, Calendar, Edit2, Trash2, X, Activity } from 'lucide-react';
import { usePreview } from '../../context/PreviewContext';
import { Button } from '../../components/ui/button';
import SupplementTimeline from '../../components/SupplementTimeline';
import TimeWindowSelector, { filterTreatmentsByTimeWindow } from '../../components/TimeWindowSelector';
import { toast } from 'sonner';

/**
 * Preview Vitamins/Treatments Page
 * Read-only treatments timeline with mock data
 */
const PreviewVitaminsPage = () => {
  const { treatments, bloodResults } = usePreview();
  const [viewingTreatment, setViewingTreatment] = useState(null);
  const [timeWindow, setTimeWindow] = useState('36m');

  // Filter treatments by time window
  const filteredTreatments = useMemo(() => {
    return filterTreatmentsByTimeWindow(treatments, bloodResults, timeWindow);
  }, [treatments, bloodResults, timeWindow]);

  // Calculate axis config for timeline based on time window
  const axisConfig = useMemo(() => {
    if (filteredTreatments.length === 0) return null;
    
    const now = Date.now();
    let domainStart, domainEnd;
    
    if (timeWindow === 'all') {
      const allDates = [
        ...filteredTreatments.map(t => new Date(t.start_date).getTime()),
        ...filteredTreatments.filter(t => t.end_date).map(t => new Date(t.end_date).getTime())
      ].filter(d => !isNaN(d));
      
      if (allDates.length === 0) return null;
      
      domainStart = Math.min(...allDates);
      domainEnd = Math.max(...allDates, now);
    } else {
      const months = timeWindow === '12m' ? 12 : timeWindow === '24m' ? 24 : 36;
      domainEnd = now;
      const startDate = new Date(now);
      startDate.setMonth(startDate.getMonth() - months);
      domainStart = startDate.getTime();
    }
    
    return {
      dataPoints: [],
      domainStartTs: domainStart,
      domainEndTs: domainEnd,
      margin: { left: 45, right: 20 }
    };
  }, [filteredTreatments, timeWindow]);

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'medication': return Pill;
      case 'supplement': return Leaf;
      case 'vitamin': return Sparkles;
      case 'other': return Pill;
      default: return Pill;
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'medication': return 'Medication';
      case 'supplement': return 'Supplement';
      case 'vitamin': return 'Vitamin';
      case 'other': return 'Other';
      default: return 'Treatment';
    }
  };

  const getCategoryColor = (category) => {
    // Match EXACT colors from SupplementTimeline for consistency
    switch (category) {
      case 'medication': return { 
        bg: 'bg-indigo-100', 
        text: 'text-indigo-700', 
        border: 'border-indigo-200',
        hex: '#4F6BED',
        badgeBg: 'bg-[#4F6BED]',
        badgeText: 'text-white'
      };
      case 'supplement': return { 
        bg: 'bg-emerald-100', 
        text: 'text-emerald-700', 
        border: 'border-emerald-200',
        hex: '#3FAE7F',
        badgeBg: 'bg-[#3FAE7F]',
        badgeText: 'text-white'
      };
      case 'vitamin': return { 
        bg: 'bg-yellow-100', 
        text: 'text-yellow-800', 
        border: 'border-yellow-300',
        hex: '#F2B705',
        badgeBg: 'bg-[#F2B705]',
        badgeText: 'text-gray-900'
      };
      case 'other': return { 
        bg: 'bg-purple-100', 
        text: 'text-purple-700', 
        border: 'border-purple-200',
        hex: '#8B5CF6',
        badgeBg: 'bg-[#8B5CF6]',
        badgeText: 'text-white'
      };
      default: return { 
        bg: 'bg-slate-50', 
        text: 'text-slate-700', 
        border: 'border-slate-200',
        hex: '#64748b',
        badgeBg: 'bg-slate-500',
        badgeText: 'text-white'
      };
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-2">Treatments</h1>
          <p className="text-base sm:text-lg leading-relaxed text-slate-700">Track your medications, supplements, and vitamins</p>
        </div>
        <Button disabled className="opacity-50 cursor-not-allowed bg-emerald-600">
          <Plus className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
          Add Treatment
        </Button>
      </div>

      {/* Timeline Section */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-slate-100">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Treatment Timeline</h2>
              <p className="text-sm text-slate-500 mt-1">Visualize when treatments started and ended</p>
            </div>
            <div className="self-end sm:self-auto">
              <TimeWindowSelector 
                selected={timeWindow} 
                onChange={setTimeWindow} 
              />
            </div>
          </div>
        </div>
        <div className="p-4">
          <SupplementTimeline
            supplements={filteredTreatments}
            axisConfig={axisConfig}
            maxHeight={250}
          />
        </div>
      </div>

      {/* Treatment Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {treatments.map(treatment => {
          const Icon = getCategoryIcon(treatment.category);
          const colors = getCategoryColor(treatment.category);
          const isOngoing = !treatment.end_date;

          return (
            <div 
              key={treatment.id} 
              className={`bg-white rounded-xl border ${colors.border} p-5`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`p-2 ${colors.bg} rounded-lg`}>
                    <Icon className={`h-5 w-5 ${colors.text}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{treatment.name}</h3>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${colors.badgeBg} ${colors.badgeText}`}>
                      {getCategoryLabel(treatment.category)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isOngoing && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                      Active
                    </span>
                  )}
                  <button
                    onClick={() => setViewingTreatment(treatment)}
                    className="p-2 text-slate-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                    title="View details"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => toast.error('Delete is disabled in preview mode')}
                    className="p-2 text-slate-300 cursor-not-allowed rounded-lg"
                    title="Delete (disabled in preview)"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-slate-600">
                  <Calendar className="h-4 w-4 text-slate-400" />
                  <span>
                    Started: {new Date(treatment.start_date).toLocaleDateString('en-AU', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </span>
                </div>
                {treatment.end_date && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Calendar className="h-4 w-4 text-slate-400" />
                    <span>
                      Ended: {new Date(treatment.end_date).toLocaleDateString('en-AU', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                )}
                {treatment.dosage && (
                  <p className="text-slate-600">
                    <span className="text-slate-400">Dosage:</span> {treatment.dosage}
                  </p>
                )}
                {treatment.frequency && (
                  <p className="text-slate-600">
                    <span className="text-slate-400">Frequency:</span> {treatment.frequency}
                  </p>
                )}
                {treatment.notes && (
                  <p className="text-slate-500 italic mt-2">{treatment.notes}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* View-Only Modal */}
      {viewingTreatment && (
        <>
          {/* Mobile: Full screen modal with backdrop */}
          <div className="lg:hidden fixed inset-0 z-50">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/50" onClick={() => setViewingTreatment(null)} />
            
            {/* Modal container - full screen */}
            <div className="relative w-full h-full bg-white overflow-y-auto flex flex-col">
              {/* Simple X close at top right */}
              <div className="flex justify-end px-4 pt-6 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setViewingTreatment(null)}
                  className="p-2 text-slate-400 hover:text-slate-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              {/* Content - spreads vertically */}
              <div className="px-4 pb-6 flex-1 flex flex-col">
                {/* Fields wrapper - spreads evenly */}
                <div className="flex-1 flex flex-col justify-evenly space-y-3">
                  {/* Category Display */}
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">Type</label>
                    <div className="grid grid-cols-4 gap-1.5">
                      {['vitamin', 'supplement', 'medication', 'other'].map((cat) => {
                        const Icon = getCategoryIcon(cat);
                        const colors = getCategoryColor(cat);
                        const isSelected = viewingTreatment.category === cat;
                        return (
                          <div
                            key={cat}
                            className={`p-2 rounded-lg border-2 flex flex-col items-center gap-1 ${
                              isSelected 
                                ? `${colors.border} ${colors.bg}` 
                                : 'border-slate-100 bg-slate-50 opacity-40'
                            }`}
                          >
                            <Icon className={`h-4 w-4 ${isSelected ? colors.text : 'text-slate-300'}`} />
                            <span className={`text-[10px] font-medium capitalize ${isSelected ? colors.text : 'text-slate-400'}`}>
                              {cat}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Name */}
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">Name</label>
                    <div className="w-full text-sm font-medium text-slate-900 bg-slate-50 rounded-lg px-3 py-2.5 border border-slate-200">
                      {viewingTreatment.name}
                    </div>
                  </div>

                  {/* Dosage & Frequency - Side by side */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1.5">Dosage</label>
                      <div className="w-full text-sm font-medium text-slate-900 bg-slate-50 rounded-lg px-3 py-2.5 border border-slate-200">
                        {viewingTreatment.dosage || '-'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1.5">Frequency</label>
                      <div className="w-full text-sm font-medium text-slate-900 bg-slate-50 rounded-lg px-3 py-2.5 border border-slate-200">
                        {viewingTreatment.frequency || '-'}
                      </div>
                    </div>
                  </div>

                  {/* Dates - Side by side */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1.5">Start Date</label>
                      <div className="w-full h-[44px] flex items-center justify-center text-sm font-medium text-slate-900 bg-slate-50 rounded-lg px-3 border border-slate-200">
                        {new Date(viewingTreatment.start_date).toLocaleDateString('en-AU', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1.5">End Date</label>
                      <div className="w-full h-[44px] flex items-center justify-center text-sm font-medium text-slate-900 bg-slate-50 rounded-lg px-3 border border-slate-200">
                        {viewingTreatment.end_date 
                          ? new Date(viewingTreatment.end_date).toLocaleDateString('en-AU', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric'
                            })
                          : 'Ongoing'
                        }
                      </div>
                    </div>
                  </div>

                  {/* Notes */}
                  {viewingTreatment.notes && (
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1.5">Notes</label>
                      <div className="w-full text-sm text-slate-900 bg-slate-50 rounded-lg px-3 py-2.5 border border-slate-200">
                        {viewingTreatment.notes}
                      </div>
                    </div>
                  )}
                </div>

                {/* Button - at bottom */}
                <div className="pt-4 mt-auto">
                  <Button 
                    onClick={() => setViewingTreatment(null)} 
                    className="w-full h-12"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Desktop: Modal with backdrop */}
          <div className="hidden lg:flex fixed inset-0 z-50 items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={() => setViewingTreatment(null)} />
            <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10">
                  <Pill className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-slate-900">View Treatment</h1>
                  <p className="text-[11px] text-slate-500">Preview mode - read only</p>
                </div>
              </div>
              
              {/* Category */}
              <div className="mb-3">
                <label className="block text-xs font-medium text-slate-700 mb-1">Type</label>
                <div className="grid grid-cols-4 gap-1.5">
                  {['vitamin', 'supplement', 'medication', 'other'].map((cat) => {
                    const Icon = getCategoryIcon(cat);
                    const colors = getCategoryColor(cat);
                    const isSelected = viewingTreatment.category === cat;
                    return (
                      <div key={cat} className={`p-2 rounded-lg border-2 flex flex-col items-center gap-0.5 ${isSelected ? `${colors.border} ${colors.bg}` : 'border-slate-100 bg-slate-50 opacity-40'}`}>
                        <Icon className={`h-4 w-4 ${isSelected ? colors.text : 'text-slate-300'}`} />
                        <span className={`text-[10px] font-medium capitalize ${isSelected ? colors.text : 'text-slate-400'}`}>{cat}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="mb-3">
                <label className="block text-xs font-medium text-slate-700 mb-1">Name</label>
                <div className="w-full text-sm font-medium text-slate-900 bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">{viewingTreatment.name}</div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Dosage</label>
                  <div className="w-full text-sm font-medium text-slate-900 bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">{viewingTreatment.dosage || '-'}</div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Frequency</label>
                  <div className="w-full text-sm font-medium text-slate-900 bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">{viewingTreatment.frequency || '-'}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Start Date</label>
                  <div className="w-full text-sm font-medium text-slate-900 bg-slate-50 rounded-lg px-3 py-2 border border-slate-200 text-center">
                    {new Date(viewingTreatment.start_date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">End Date</label>
                  <div className="w-full text-sm font-medium text-slate-900 bg-slate-50 rounded-lg px-3 py-2 border border-slate-200 text-center">
                    {viewingTreatment.end_date ? new Date(viewingTreatment.end_date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Ongoing'}
                  </div>
                </div>
              </div>

              {viewingTreatment.notes && (
                <div className="mb-3">
                  <label className="block text-xs font-medium text-slate-700 mb-1">Notes</label>
                  <div className="w-full text-sm text-slate-900 bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">{viewingTreatment.notes}</div>
                </div>
              )}

              <Button onClick={() => setViewingTreatment(null)} className="w-full">Close</Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PreviewVitaminsPage;
